import { useState, useCallback, useEffect, useMemo } from 'react';
import type { MapInstance, MapOptions } from 'src/types/map-provider';
import { MapProviderError } from 'src/types/map-provider';
import type { UseMapInstanceOptions, UseMapInstanceReturn } from 'src/types/map-hooks';

/**
 * Hook for creating and managing map instances
 *
 * Handles map instance creation, lifecycle management, and cleanup.
 * Requires a loaded provider to create maps.
 *
 * @example
 * ```tsx
 * const { mapInstance, createMap } = useMapInstance({
 *   provider: provider,
 *   mapOptions: { zoom: 12 },
 * });
 *
 * // Later in component
 * const handleCreateMap = async () => {
 *   const instance = await createMap(mapElement, { center: { lat: 40, lng: -74 } });
 * };
 * ```
 *
 * @param options - Configuration options
 * @param options.provider - Map provider instance (required for map creation)
 * @param options.mapOptions - Default map options
 * @param options.onError - Callback for error handling
 *
 * @returns Map instance state and creation API
 */
export function useMapInstance(options: UseMapInstanceOptions): UseMapInstanceReturn {
  const { provider, mapOptions, containerElement, autoCreate, onError } = options;

  // State
  const [mapInstance, setMapInstance] = useState<MapInstance | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<MapProviderError | null>(null);

  // Create map instance
  const createMap = useCallback(
    async (element: HTMLElement, options?: MapOptions): Promise<MapInstance> => {
      if (!provider) {
        const providerError = new MapProviderError('Map provider not loaded');
        setError(providerError);
        onError?.(providerError);
        throw providerError;
      }

      const finalMapOptions: MapOptions = {
        center: { lat: 52.520008, lng: 13.404954 }, // Default: Berlin
        zoom: 10,
        ...mapOptions,
        ...options,
      };

      setIsCreating(true);
      setError(null);

      try {
        const instance = await provider.createMapInstance(element, finalMapOptions);
        setMapInstance(instance);
        setIsCreating(false);
        return instance;
      } catch (err) {
        const mapError =
          err instanceof MapProviderError
            ? err
            : new MapProviderError(
                `Failed to create map: ${err instanceof Error ? err.message : String(err)}`
              );
        setError(mapError);
        setIsCreating(false);
        onError?.(mapError);
        throw mapError;
      }
    },
    [provider, mapOptions, onError]
  );

  // Cleanup
  const cleanup = useCallback(() => {
    if (mapInstance) {
      // Note: Map instances don't have explicit destroy methods
      // Just clear the reference
      setMapInstance(null);
    }
    setError(null);
  }, [mapInstance]);

  // Auto-creation effect
  useEffect(() => {
    if (autoCreate && provider && containerElement && !mapInstance && !isCreating) {
      createMap(containerElement).catch((err) => {
        console.error('Auto-create map failed:', err);
      });
    }
  }, [autoCreate, provider, containerElement, mapInstance, isCreating, createMap]);

  // Lifecycle management
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  // Return memoized value
  const memoizedValue = useMemo<UseMapInstanceReturn>(
    () => ({
      mapInstance,
      isCreating,
      error,
      createMap,
      cleanup,
    }),
    [mapInstance, isCreating, error, createMap, cleanup]
  );

  return memoizedValue;
}
